import type { MouseEvent } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { PictoMoto } from '@/assets/icons-april-fr/pictos';
import Text from '@/components/forms/text';
import { AUTH_GRAVITEE_ENABLED } from '@/lib/constants';
import { useForgotPassword } from '@/hooks/auth/use-auth-recovery';
import { useWhitelabelByHost } from '@/hooks/api/use-whitelabel';
import usePageTitle from '@/hooks/use-page-title';
import { initiateGraviteeLogin } from '@/lib/auth/gravitee';

interface OubliMdpValues {
    email: string;
}

const OubliMdp = () => {
    const forgotPassword = useForgotPassword();
    const name = useWhitelabelByHost().data?.name;
    usePageTitle('Mot de passe oublié');

    const { register, handleSubmit, watch } = useForm<OubliMdpValues>({ defaultValues: { email: '' } });

    const postForgotPassword = (values: OubliMdpValues) =>
        forgotPassword.mutate({
            ...values,
            callback_url: `${window.location.origin}/login/nouveau-mot-de-passe`,
        });

    const handleBack = (e: MouseEvent<HTMLAnchorElement>) => {
        if (AUTH_GRAVITEE_ENABLED) {
            e.preventDefault()
            void initiateGraviteeLogin()
        }
    }

    return (
        <form onSubmit={handleSubmit(values => postForgotPassword(values))}>

            <h1 className={'mb-6 font-bold'} style={{fontSize: '1.75rem'}}><PictoMoto style={{width: 42, height: 42, verticalAlign: 'middle', display: 'inline-block', marginRight: 8}} />Je n'ai pas mon <span className={'text-[oklch(from_var(--color-primary)_calc(l*0.85)_c_h)]'}>mot de passe</span> ou je l'ai <span className={'text-[oklch(from_var(--color-primary)_calc(l*0.85)_c_h)]'}>oublié</span></h1>

            <p className={'font-bold inline-block mb-4'}>C'est votre 1ère connexion ou vous avez oublié votre mot de passe ? On vous accompagne !</p>

            <p className={'inline-block mt-2 mb-4'}>Saisissez <b>l'adresse email</b> associée à votre contrat {name} et cliquez sur "Envoyez-moi mon mot de passe".</p>
            <p className={'inline-block mb-4'}>Vous allez recevoir un <b>email</b> avec un <b>nouveau mot de passe</b>, vous permettant de vous connecter à votre espace personnel.</p>

            <div className={'mb-6 mt-2'}>
                <Text
                    type={'text'}
                    autoComplete="email"
                    {...register('email')}
                    value={watch('email')}
                    required
                    label={'Votre adresse mail personnelle'}
                />
            </div>

            <div className="grid gap-2">
                <button type="submit" className={'inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground border-none hover:bg-primary-hover'}>
                    Envoyez-moi mon mot de passe
                </button>
                <Link to={'/login'} onClick={handleBack} className={'inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-transparent text-primary border border-primary hover:bg-transparent hover:text-foreground hover:border-foreground mt-6'}>
                    Retourner à la page de connexion
                </Link>
            </div>

        </form>
    );
};

export default OubliMdp;
